import axios from 'axios';
import { AuthUser, LoginRequest, LoginResponse } from '../types/api';
import { API_ENDPOINTS } from '../config/apiConfig';

const authApi = axios.create({
  baseURL: API_ENDPOINTS.authApi,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await authApi.post('/login', credentials);
      const authResponse = response.data.data; // AuthResponse is nested in ApiResponse

      // A user may hold several portals; roles is authoritative, role is the legacy fallback.
      const portals: string[] = authResponse.roles?.length ? authResponse.roles : [authResponse.role];
      if (!portals.includes('CMS_ADMIN')) {
        // Log them out immediately if they don't have CMS_ADMIN role
        throw new Error('Access denied. CMS access requires CMS_ADMIN role.');
      }

      // Transform to expected LoginResponse format
      const loginResponse: LoginResponse = {
        user: {
          id: authResponse.userId,
          username: authResponse.email, // Use email as username
          email: authResponse.email,
          fullName: authResponse.fullName,
          role: authResponse.role,
        },
        token: authResponse.accessToken,
        refreshToken: authResponse.refreshToken,
        expiresIn: authResponse.expiresIn || 3600,
      };

      return loginResponse;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Invalid credentials');
      } else if (error.message.includes('CMS_ADMIN')) {
        throw error; // Re-throw role-based access error
      } else {
        throw new Error('Login failed. Please try again.');
      }
    }
  },

  async logout(): Promise<void> {
    try {
      await authApi.post('/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    }
  },

  async verifyToken(token: string): Promise<AuthUser> {
    try {
      const response = await authApi.get('/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      const userResponse = response.data.data; // UserResponse is nested in ApiResponse

      // A user may hold several portals; roles is authoritative, role is the legacy fallback.
      const portals: string[] = userResponse.roles?.length ? userResponse.roles : [userResponse.role];
      if (!portals.includes('CMS_ADMIN')) {
        throw new Error('Access denied. CMS access requires CMS_ADMIN role.');
      }

      // Transform to AuthUser format
      const user: AuthUser = {
        id: userResponse.id,
        username: userResponse.userName,
        email: userResponse.email,
        fullName: userResponse.fullName,
        role: userResponse.role,
      };

      return user;
    } catch (error: any) {
      if (error.response?.status === 401 || error.message.includes('CMS_ADMIN')) {
        localStorage.removeItem('authToken');
        throw new Error('Session expired or access denied');
      }
      throw error;
    }
  },

  async refreshToken(): Promise<LoginResponse> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const response = await authApi.post('/refresh-token', { refreshToken });

      const authResponse = response.data.data; // AuthResponse is nested in ApiResponse

      // Check if user still has CMS_ADMIN role
      if (authResponse.role !== 'CMS_ADMIN') {
        throw new Error('Access denied. CMS access requires CMS_ADMIN role.');
      }

      // Transform to expected LoginResponse format
      const loginResponse: LoginResponse = {
        user: {
          id: authResponse.userId,
          username: authResponse.email,
          email: authResponse.email,
          fullName: authResponse.fullName,
          role: authResponse.role,
        },
        token: authResponse.accessToken,
        refreshToken: authResponse.refreshToken,
        expiresIn: authResponse.expiresIn || 3600,
      };

      return loginResponse;
    } catch (error: any) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  },

  async getCurrentUser(): Promise<AuthUser> {
    try {
      const response = await authApi.get('/me');
      const userResponse = response.data.data; // UserResponse is nested in ApiResponse

      // Check if user has CMS_ADMIN role
      if (userResponse.role !== 'CMS_ADMIN') {
        throw new Error('Access denied. CMS access requires CMS_ADMIN role.');
      }

      // Transform to AuthUser format
      const user: AuthUser = {
        id: userResponse.id,
        username: userResponse.userName,
        email: userResponse.email,
        fullName: userResponse.fullName,
        role: userResponse.role,
      };

      return user;
    } catch (error: any) {
      if (error.message.includes('CMS_ADMIN')) {
        localStorage.removeItem('authToken');
      }
      throw error;
    }
  },
};
